import React, { useState } from 'react';
import { Star, X } from 'lucide-react';
import { Z_LAYERS } from '../constants/zLayers';
import {
  SUPPORT_EMAIL,
  declineRatingPrompt,
  deferRatingPrompt,
  openReviewPage,
} from '../utils/ratingPrompt';

interface RatingPromptProps {
  isVisible: boolean;
  onClose: () => void;
}

/**
 * Dismissible "rate us" card, shown after the user has copied/inserted enough
 * prompts to have gotten real value (see utils/ratingPrompt.ts for the
 * trigger logic). Happy users are deep-linked to the Chrome Web Store review
 * page; unhappy users are routed to support instead.
 */
export default function RatingPrompt({ isVisible, onClose }: RatingPromptProps) {
  const [step, setStep] = useState<'ask' | 'feedback'>('ask');

  if (!isVisible) return null;

  // The X counts as "maybe later" — re-ask after more use, don't nag now.
  const handleLater = () => {
    void deferRatingPrompt();
    onClose();
  };

  const handleRate = () => {
    void openReviewPage().finally(onClose);
  };

  // Entering the feedback step already counts as "don't ask again".
  const handleNotReally = () => {
    void declineRatingPrompt();
    setStep('feedback');
  };

  return (
    <div
      className="absolute bottom-4 left-0 right-0 mx-auto w-fit max-w-sm bg-gray-800/90 backdrop-blur-sm text-white px-4 py-3 rounded-xl shadow-lg animate-fade-in border border-gray-700/50"
      style={{ zIndex: Z_LAYERS.TOAST }}
      role="dialog"
      aria-label="Rate Top Prompt Library"
    >
      <button
        onClick={step === 'ask' ? handleLater : onClose}
        className="absolute top-2 right-2 p-1 rounded-md text-gray-400 hover:text-white hover:bg-gray-700/50"
        aria-label="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      {step === 'ask' ? (
        <div className="pr-5">
          <div className="flex items-center gap-2 mb-2">
            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
            <span className="font-medium">Enjoying Top Prompt Library?</span>
          </div>
          <p className="text-sm text-gray-300 mb-3">
            A quick rating on the Chrome Web Store helps others find it.
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRate}
              className="px-3 py-1.5 rounded-md text-sm font-medium bg-indigo-600 hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            >
              Rate it
            </button>
            <button
              onClick={handleLater}
              className="px-3 py-1.5 rounded-md text-sm text-gray-300 hover:text-white hover:bg-gray-700/50"
            >
              Maybe later
            </button>
            <button
              onClick={handleNotReally}
              className="px-3 py-1.5 rounded-md text-sm text-gray-300 hover:text-white hover:bg-gray-700/50"
            >
              Not really
            </button>
          </div>
        </div>
      ) : (
        <div className="pr-5">
          <div className="font-medium mb-2">Sorry to hear that!</div>
          <p className="text-sm text-gray-300 mb-3">
            Tell us what to improve — we read every message.
          </p>
          <div className="flex items-center gap-2">
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Top Prompt Library feedback')}`}
              onClick={onClose}
              className="px-3 py-1.5 rounded-md text-sm font-medium bg-indigo-600 hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            >
              Email support
            </a>
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-md text-sm text-gray-300 hover:text-white hover:bg-gray-700/50"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
